// InvitationManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Mail, 
  Clock, 
  CheckCircle, 
  XCircle, 
  RefreshCw, 
  AlertTriangle,
  Trash2,
  Send,
  Shield,
  MapPin,
  User
} from 'lucide-react';

// Import the utility functions
import { 
  getPendingInvitations, 
  cancelInvitation, 
  resendInvitation, 
  sendInvitationReminder 
} from '../access/accessControlUtils';

// Import generic components
import Tooltip from '../../generic_components/Tooltip/Tooltip';
import Table from '../../generic_components/Table/Table';

const InvitationManagement = ({ selectedBusiness }) => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  
  // Add window width state for responsive design
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Responsive helpers
  const isMobile = windowWidth <= 768;
  const isSmallMobile = windowWidth <= 480;

  const loadInvitations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await getPendingInvitations(selectedBusiness);
      setInvitations(data);
      
    } catch (error) {
      console.error('Error loading invitations:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness]);

  // Load invitations when component mounts or business changes
  useEffect(() => {
    if (selectedBusiness) {
      loadInvitations();
    }
  }, [selectedBusiness, loadInvitations]);

  const handleCancelInvitation = async (invitationId) => {
    if (!window.confirm('Are you sure you want to cancel this invitation? This action cannot be undone.')) {
      return;
    }

    try {
      setProcessingId(invitationId);
      
      await cancelInvitation(invitationId, selectedBusiness);
      
      // Remove invitation from list
      setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
      
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      setError(error.message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      setProcessingId(invitationId);
      
      const inviter_email = localStorage.getItem('email');
      await resendInvitation(invitationId, selectedBusiness, inviter_email);
      
      // Refresh the list to update email status
      await loadInvitations();
      
    } catch (error) {
      console.error('Error resending invitation:', error);
      setError(error.message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleSendReminder = async (invitationId) => {
    try {
      setProcessingId(invitationId);
      
      await sendInvitationReminder(invitationId, selectedBusiness);
      
      // Show success message (you could add a toast notification here)
      console.log('Reminder sent successfully');
      
    } catch (error) {
      console.error('Error sending reminder:', error);
      setError(error.message);
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getTimeUntilExpiry = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry - now;
    
    if (diff <= 0) return 'Expired';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days} day${days !== 1 ? 's' : ''} left`;
    if (hours > 0) return `${hours} hour${hours !== 1 ? 's' : ''} left`;
    return 'Less than 1 hour left';
  };

  const getStatusColor = (status, isExpired) => {
    if (isExpired || status === 'expired') return '#ef4444';
    if (status === 'pending') return '#f59e0b';
    if (status === 'confirmed') return '#10b981';
    return '#6b7280';
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'tla': return { backgroundColor: 'rgba(168, 85, 247, 0.2)', color: '#c4b5fd', border: '1px solid rgba(168, 85, 247, 0.3)' };
      case 'admin': return { backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#fca5a5', border: '1px solid rgba(239, 68, 68, 0.3)' };
      case 'supervisor': return { backgroundColor: 'rgba(59, 130, 246, 0.2)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)' };
      case 'technician': return { backgroundColor: 'rgba(34, 197, 94, 0.2)', color: '#86efac', border: '1px solid rgba(34, 197, 94, 0.3)' };
      default: return { backgroundColor: 'rgba(156, 163, 175, 0.2)', color: '#d1d5db', border: '1px solid rgba(156, 163, 175, 0.3)' };
    }
  };

  const styles = {
    container: {
      // backgroundColor: 'rgba(255, 255, 255, 0.08)',
      backdropFilter: 'blur(12px)',
      borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      marginBottom: '24px'
    },
    header: {
      padding: isMobile ? '16px' : '24px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'stretch' : 'center',
      justifyContent: 'space-between',
      gap: '12px'
    },
    title: {
      fontSize: isMobile ? '18px' : '20px',
      fontWeight: '600',
      color: 'white',
      margin: '0',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    refreshButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      backgroundColor: 'rgba(59, 130, 246, 0.8)',
      color: 'white',
      padding: '8px 16px',
      borderRadius: '8px',
      border: '1px solid rgba(59, 130, 246, 0.5)',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      width: isMobile ? '100%' : 'auto'
    },
    errorCard: {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '8px',
      padding: '12px',
      margin: '16px 24px',
      color: '#fca5a5'
    },
    // Mobile card styles
    cardsContainer: {
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    },
    invitationCard: {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      borderRadius: '12px',
      padding: '16px',
      transition: 'all 0.2s'
    },
    cardHeader: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      marginBottom: '16px'
    },
    cardUserInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    },
    cardUserName: {
      fontSize: '16px',
      fontWeight: '500',
      color: 'white',
      wordBreak: 'break-word'
    },
    cardUserEmail: {
      fontSize: '14px',
      color: '#d1d5db',
      wordBreak: 'break-all'
    },
    cardDetailsGrid: {
      display: 'grid',
      gridTemplateColumns: isSmallMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: '12px',
      marginBottom: '16px'
    },
    cardDetailItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    },
    cardDetailLabel: {
      fontSize: '12px',
      color: '#9ca3af',
      fontWeight: '500'
    },
    cardDetailValue: {
      fontSize: '14px',
      color: '#e5e7eb',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    cardActions: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap'
    },
    cardActionButton: {
      flex: '1 1 calc(33.333% - 6px)',
      minWidth: '100px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      padding: '8px 12px',
      borderRadius: '6px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s',
      fontSize: '14px',
      fontWeight: '500',
      color: 'white'
    },
    resendButton: {
      backgroundColor: 'rgba(59, 130, 246, 0.2)',
      color: '#93c5fd',
      border: '1px solid rgba(59, 130, 246, 0.3)'
    },
    reminderButton: {
      backgroundColor: 'rgba(245, 158, 11, 0.2)',
      color: '#fbbf24',
      border: '1px solid rgba(245, 158, 11, 0.3)'
    },
    cancelButton: {
      backgroundColor: 'rgba(239, 68, 68, 0.2)',
      color: '#fca5a5',
      border: '1px solid rgba(239, 68, 68, 0.3)'
    },
    // Desktop table styles
    tableWrapper: {
      overflowX: 'auto'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    tableHeader: {
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.15)'
    },
    th: {
      textAlign: 'left',
      padding: '16px 24px',
      fontWeight: '600',
      color: 'white'
    },
    tr: {
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      transition: 'background-color 0.2s'
    },
    td: {
      padding: '16px 24px'
    },
    userInfo: {
      display: 'flex',
      flexDirection: 'column'
    },
    userName: {
      fontWeight: '500',
      color: 'white',
      margin: '0'
    },
    userEmail: {
      fontSize: '14px',
      color: '#d1d5db',
      margin: '0'
    },
    roleBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      borderRadius: '9999px',
      fontSize: '12px',
      fontWeight: '500'
    },
    statusContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    },
    statusBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      borderRadius: '9999px',
      fontSize: '12px',
      fontWeight: '500',
      width: 'fit-content'
    },
    timeText: {
      fontSize: '12px',
      color: '#9ca3af'
    },
    actionsContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    actionButton: {
      padding: '8px',
      color: '#d1d5db',
      transition: 'all 0.2s',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '32px',
      minHeight: '32px'
    },
    actionButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    loadingOverlay: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px',
      color: 'white',
      fontSize: '16px'
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px',
      color: '#9ca3af',
      textAlign: 'center'
    },
    emptyStateIcon: {
      width: '48px',
      height: '48px',
      marginBottom: '16px',
      color: '#6b7280'
    },
    emptyStateTitle: {
      fontSize: '18px',
      fontWeight: '500',
      color: 'white',
      marginBottom: '8px'
    },
    emptyStateText: {
      fontSize: '14px',
      color: '#9ca3af'
    },
    sitesContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px'
    },
    siteText: {
      fontSize: '12px',
      color: '#d1d5db'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>
            <Mail style={{width: '20px', height: '20px'}} />
            Pending Invitations
          </h2>
        </div>
        <div style={styles.loadingOverlay}>
          <RefreshCw style={{width: '20px', height: '20px', marginRight: '8px'}} className="animate-spin" />
          Loading invitations...
        </div>
      </div>
    );
  }

  // Mobile Card View
  const renderMobileView = () => (
    <div style={styles.cardsContainer}>
      {invitations.map((invitation) => (
        <div key={invitation.id} style={styles.invitationCard}>
          <div style={styles.cardHeader}>
            <div style={styles.cardUserInfo}>
              <div style={styles.cardUserName}>{invitation.name}</div>
              <div style={styles.cardUserEmail}>{invitation.email}</div>
            </div>
            
            <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
              <span style={{...styles.roleBadge, ...getRoleColor(invitation.role)}}>
                <Shield style={{width: '12px', height: '12px'}} />
                {invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)}
              </span>
              <span style={{
                ...styles.statusBadge,
                backgroundColor: invitation.isExpired ? 'rgba(239, 68, 68, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                color: getStatusColor(invitation.status, invitation.isExpired),
                border: `1px solid ${getStatusColor(invitation.status, invitation.isExpired)}30`
              }}>
                {invitation.isExpired ? (
                  <>
                    <XCircle style={{width: '12px', height: '12px'}} />
                    Expired
                  </>
                ) : (
                  <>
                    <Clock style={{width: '12px', height: '12px'}} />
                    Pending
                  </>
                )}
              </span>
            </div>
          </div>

          <div style={styles.cardDetailsGrid}>
            <div style={styles.cardDetailItem}>
              <span style={styles.cardDetailLabel}>Sites</span>
              <span style={styles.cardDetailValue}>
                <MapPin style={{width: '14px', height: '14px'}} />
                {invitation.sites && invitation.sites.length > 0 
                  ? invitation.sites.join(', ')
                  : (invitation.role === 'admin' || invitation.role === 'supervisor' || invitation.role === 'tla' 
                    ? 'All Sites' 
                    : 'No sites assigned')}
              </span>
            </div>
            
            <div style={styles.cardDetailItem}>
              <span style={styles.cardDetailLabel}>Invited</span>
              <span style={styles.cardDetailValue}>
                <User style={{width: '14px', height: '14px'}} />
                {invitation.invitedByEmail}
              </span>
            </div>
            
            <div style={styles.cardDetailItem}>
              <span style={styles.cardDetailLabel}>Expires</span>
              <span style={{...styles.cardDetailValue, color: invitation.isExpired ? '#ef4444' : '#f59e0b'}}>
                <Clock style={{width: '14px', height: '14px'}} />
                {getTimeUntilExpiry(invitation.expiresAt)}
              </span>
            </div>
            
            <div style={styles.cardDetailItem}>
              <span style={styles.cardDetailLabel}>Email Status</span>
              <span style={styles.cardDetailValue}>
                {invitation.emailSent ? (
                  <>
                    <CheckCircle style={{width: '14px', height: '14px', color: '#10b981'}} />
                    Email sent
                  </>
                ) : (
                  <>
                    <XCircle style={{width: '14px', height: '14px', color: '#ef4444'}} />
                    Email failed
                  </>
                )}
              </span>
            </div>
          </div>

          {!invitation.isExpired && invitation.status === 'pending' && (
            <div style={styles.cardActions}>
              <button
                style={{
                  ...styles.cardActionButton,
                  ...styles.resendButton,
                  ...(processingId === invitation.id ? styles.actionButtonDisabled : {})
                }}
                onClick={() => handleResendInvitation(invitation.id)}
                disabled={processingId === invitation.id}
              >
                <Send style={{width: '16px', height: '16px'}} />
                Resend
              </button>
              
              <button
                style={{
                  ...styles.cardActionButton,
                  ...styles.reminderButton,
                  ...(processingId === invitation.id ? styles.actionButtonDisabled : {})
                }}
                onClick={() => handleSendReminder(invitation.id)}
                disabled={processingId === invitation.id}
              >
                <AlertTriangle style={{width: '16px', height: '16px'}} />
                Remind
              </button>
              
              <button
                style={{
                  ...styles.cardActionButton,
                  ...styles.cancelButton,
                  ...(processingId === invitation.id ? styles.actionButtonDisabled : {})
                }}
                onClick={() => handleCancelInvitation(invitation.id)}
                disabled={processingId === invitation.id}
              >
                <Trash2 style={{width: '16px', height: '16px'}} />
                Cancel
              </button>
            </div>
          )}
          
          {processingId === invitation.id && (
            <div style={{display: 'flex', justifyContent: 'center', marginTop: '12px'}}>
              <RefreshCw style={{width: '16px', height: '16px', color: '#60a5fa'}} className="animate-spin" />
            </div>
          )}
        </div>
      ))}
    </div>
  );

  // Desktop Table View
  const renderDesktopView = () => (
    <div style={styles.tableWrapper}>
      <Table columns={pending_invitations_columns} data={pending_invitations_data}/>
    </div>
  );

  const pending_invitations_columns = [
    { key: 'user', label: 'User' },
    { key: 'role', label: 'Role' },
    { key: 'sites', label: 'Sites' },
    { key: 'status', label: 'Status' },
    { key: 'invited', label: 'Invited' },
    { key: 'expires', label: 'Expires' },
    { key: 'actions', label: 'Actions' }
  ];

  const pending_invitations_data = invitations.map((invitation) => (
    {
      user: 
            (<div>
                <div>{invitation.name}</div>
                <div>{invitation.email}</div>
            </div>),
      role: 
            (<div>
              <span style={{...styles.roleBadge, ...getRoleColor(invitation.role)}}>
                {invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)}
              </span>
            </div>),
      sites: 
            (<div>
                <div>
                  {invitation.sites && invitation.sites.length > 0 ? (
                    invitation.sites.map((site, index) => (
                      <span key={index} style={styles.siteText}>{site}</span>
                    ))
                  ) : (
                    <span style={styles.siteText}>
                      {invitation.role === 'admin' || invitation.role === 'supervisor' || invitation.role === 'tla' 
                        ? 'All Sites' 
                        : 'No sites assigned'}
                    </span>
                  )}
                </div>
            </div>),
      status: 
            (
              <div style={styles.statusContainer}>
                <span style={{
                  ...styles.statusBadge,
                  backgroundColor: invitation.isExpired ? 'rgba(239, 68, 68, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                  color: getStatusColor(invitation.status, invitation.isExpired),
                  border: `1px solid ${getStatusColor(invitation.status, invitation.isExpired)}30`
                }}>
                  {invitation.isExpired ? (
                    <>
                      <XCircle style={{width: '12px', height: '12px'}} />
                      Expired
                    </>
                  ) : invitation.status === 'pending' ? (
                    <>
                      <Clock style={{width: '12px', height: '12px'}} />
                      Pending
                    </>
                  ) : (
                    <>
                      <CheckCircle style={{width: '12px', height: '12px'}} />
                      {invitation.status.charAt(0).toUpperCase() + invitation.status.slice(1)}
                    </>
                  )}
                </span>
                {invitation.emailSent ? (
                  <span style={styles.timeText}>✓ Email sent</span>
                ) : (
                  <span style={{...styles.timeText, color: '#ef4444'}}>✗ Email failed</span>
                )}
              </div>
            ),
      invited:
          (
            <div style={styles.statusContainer}>
              <span style={styles.timeText}>{formatDate(invitation.createdTime)}</span>
              <span style={styles.timeText}>by {invitation.invitedByEmail}</span>
            </div>
          ),
      expires:
          (
            <div style={styles.statusContainer}>
              <span style={styles.timeText}>{formatDate(invitation.expiresAt)}</span>
              <span style={{
                ...styles.timeText,
                color: invitation.isExpired ? '#ef4444' : '#f59e0b'
              }}>
                {getTimeUntilExpiry(invitation.expiresAt)}
              </span>
            </div>
          ),
      actions:
          (
            <div>
              {!invitation.isExpired && invitation.status === 'pending' && (
                <>
                  <Tooltip content="Resend invitation email" placement="top">
                    <button
                      style={{
                        ...styles.actionButton,
                        ...(processingId === invitation.id ? styles.actionButtonDisabled : {})
                      }}
                      onClick={() => handleResendInvitation(invitation.id)}
                      disabled={processingId === invitation.id}
                      onMouseOver={(e) => !e.target.disabled && (e.target.style.color = '#60a5fa')}
                      onMouseOut={(e) => !e.target.disabled && (e.target.style.color = '#d1d5db')}
                    >
                      <Send style={{width: '16px', height: '16px'}} />
                    </button>
                  </Tooltip>

                  <Tooltip content="Send reminder email" placement="top">
                    <button
                      style={{
                        ...styles.actionButton,
                        ...(processingId === invitation.id ? styles.actionButtonDisabled : {})
                      }}
                      onClick={() => handleSendReminder(invitation.id)}
                      disabled={processingId === invitation.id}
                      onMouseOver={(e) => !e.target.disabled && (e.target.style.color = '#facc15')}
                      onMouseOut={(e) => !e.target.disabled && (e.target.style.color = '#d1d5db')}
                    >
                      <AlertTriangle style={{width: '16px', height: '16px'}} />
                    </button>
                  </Tooltip>
                </>
              )}

              {invitation.status === 'pending' && (
                <Tooltip content="Cancel invitation" placement="top">
                  <button
                    style={{
                      ...styles.actionButton,
                      ...(processingId === invitation.id ? styles.actionButtonDisabled : {})
                    }}
                    onClick={() => handleCancelInvitation(invitation.id)}
                    disabled={processingId === invitation.id}
                    onMouseOver={(e) => !e.target.disabled && (e.target.style.color = '#f87171')}
                    onMouseOut={(e) => !e.target.disabled && (e.target.style.color = '#d1d5db')}
                  >
                    <Trash2 style={{width: '16px', height: '16px'}} />
                  </button>
                </Tooltip>
              )}

              {processingId === invitation.id && (
                <Tooltip content="Processing..." placement="top">
                  <RefreshCw style={{width: '16px', height: '16px', color: '#60a5fa'}} className="animate-spin" />
                </Tooltip>
              )}
            </div>
          )
        }
    ));
  

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>
          <Mail style={{width: '20px', height: '20px'}} />
          Pending Invitations ({invitations.length})
        </h2>
        <Tooltip text="Refresh invitation list">
          <button
            style={styles.refreshButton}
            onClick={loadInvitations}
            onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(37, 99, 235, 0.8)'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.8)'}
          >
            <RefreshCw style={{width: '16px', height: '16px'}} />
            Refresh
          </button>
        </Tooltip>
      </div>

      {error && (
        <div style={styles.errorCard}>
          {error}
        </div>
      )}

      {invitations.length === 0 ? (
        <div style={styles.emptyState}>
          <CheckCircle style={styles.emptyStateIcon} />
          <h3 style={styles.emptyStateTitle}>No Pending Invitations</h3>
          <p style={styles.emptyStateText}>
            All invitations have been processed or there are no pending invitations for this business.
          </p>
        </div>
      ) : (
        isMobile ? renderMobileView() : renderDesktopView()
      )}
    </div>
  );
};

export default InvitationManagement;